/**
 * useWorkOrderActions Hooks
 *
 * React Query mutations for the bulk work-order action endpoints.
 *
 * The UI (stepper modal) speaks a simpler per-job shape — `{ id, comments }` —
 * because that's all the user actually fills in. These hooks translate that
 * into the backend's richer `{ rejections | requests }` payload by stamping
 * `createdBy` from the current auth session and `createdDate` from the client
 * clock. `externalId` is intentionally omitted: the backend reads it from the
 * job row when validating, so shipping it from the client would be redundant
 * (and the jobs list doesn't carry it anyway).
 *
 * All three hooks invalidate the jobs list so rows whose status changed
 * re-render from the server. We invalidate on every resolved mutation
 * because even partial-success responses mutate some rows.
 */

import { useAuth } from '@/domains/auth';
import { queryKeys } from '@/lib/query-client';
import type { OutboundMoreInfoRequestedBody, OutboundRejectedBody } from '@skewb-works/contracts';
import type { OutboundAllocatedBody } from '@skewb-works/contracts';
import type { OutboundDurationChallengeBody } from '@skewb-works/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AbortWorkOrderBody } from '../api/workOrdersApi';
import {
  abortWorkOrder,
  acceptWorkOrders,
  allocateWorkOrder,
  cancelFromAbort,
  cancelWorkOrder,
  durationChallengeWorkOrder,
  engineerEnRoute,
  engineerOnSite,
  engineeringComplete,
  rejectWorkOrders,
  replanFromAbort,
  replyToMoreInfoWorkOrders,
  requestMoreInfoWorkOrders,
  scheduleWorkOrder,
  unscheduleWorkOrder,
  validationRequested,
} from '../api/workOrdersApi';

/**
 * Per-job input collected by the bulk stepper modal. Kept intentionally
 * minimal — enrichment happens inside each hook just before the HTTP call.
 */
export interface BulkJobInput {
  id: number;
  externalId: string;
  comments: string;
  attachments?: OutboundRejectedBody['rejections'][number]['attachments'];
}

function invalidateJobsList(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
  queryClient.invalidateQueries({ queryKey: queryKeys.jobs.workOrderStatuses() });
}

/**
 * Business-rule refusal check shared by the directional mutations whose
 * response resolves 200 even on failure (`{ result: { success, message } }`).
 * Throwing here — rather than returning a falsy result — lets
 * `useMutation`'s `isError`/`onError` reflect a business-rule refusal the
 * same way a network/HTTP failure would.
 */
function assertSuccess(result: { success: boolean; message: string }): void {
  if (!result.success) {
    throw new Error(result.message);
  }
}

/**
 * Build a single-note payload entry from the UI's `{id, comments}` pair. The
 * backend accepts an array of notes per job but the stepper captures only one
 * free-text field, so we always wrap it in a length-1 array.
 */
function buildNoteItem(
  input: BulkJobInput,
  createdBy: string,
): OutboundRejectedBody['rejections'][number] {
  return {
    id: input.id,
    externalId: input.externalId,
    notes: [
      {
        note: input.comments,
        createdBy,
        createdDate: new Date().toISOString(),
      },
    ],
    attachments: input.attachments,
  };
}

export function useAcceptWorkOrders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => acceptWorkOrders(ids),
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      resourceName: 'Job',
      action: 'bulk',
      count: (variables: number[]) => variables.length,
      successTitle: 'Jobs Accepted',
    },
  });
}

export function useRejectWorkOrders() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: (items: BulkJobInput[]) => {
      const rejections: OutboundRejectedBody['rejections'] = items.map((item) =>
        buildNoteItem(item, createdBy),
      );
      return rejectWorkOrders(rejections);
    },
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      resourceName: 'Job',
      action: 'bulk',
      count: (variables: BulkJobInput[]) => variables.length,
      successTitle: 'Jobs Rejected',
    },
  });
}

export function useRequestMoreInfoWorkOrders() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: (items: BulkJobInput[]) => {
      const requests: OutboundMoreInfoRequestedBody['requests'] = items.map((item) =>
        buildNoteItem(item, createdBy),
      );
      return requestMoreInfoWorkOrders(requests);
    },
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'More Info Requested',
      successMessage: (variables: BulkJobInput[]) =>
        `Requested more info for ${variables.length} ${variables.length === 1 ? 'job' : 'jobs'}`,
    },
  });
}

export function useReplyMoreInfoWorkOrders() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: (items: BulkJobInput[]) => {
      const requests: OutboundMoreInfoRequestedBody['requests'] = items.map((item) =>
        buildNoteItem(item, createdBy),
      );
      return replyToMoreInfoWorkOrders(requests);
    },
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Reply Sent',
      successMessage: (variables: BulkJobInput[]) =>
        `Replied to ${variables.length} ${variables.length === 1 ? 'job' : 'jobs'}`,
    },
  });
}

export function useScheduleWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => scheduleWorkOrder(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Scheduled',
      successMessage: 'Job moved to scheduled status',
    },
  });
}

export function useAllocateWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OutboundAllocatedBody) => allocateWorkOrder(data),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Allocated',
      successMessage: 'Job assigned to team successfully',
    },
  });
}

export function useUnscheduleWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => unscheduleWorkOrder(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Unscheduled',
      successMessage: 'Job removed from schedule',
    },
  });
}

export function useAbortWorkOrder() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => {
      const body: AbortWorkOrderBody = {
        id,
        notes: [
          {
            id: crypto.randomUUID(),
            note,
            createdBy,
            createdDate: new Date().toISOString(),
          },
        ],
      };
      return abortWorkOrder(body);
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Aborted',
      successMessage: 'Job status changed to aborted',
    },
  });
}

export function useEngineerEnRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const data = await engineerEnRoute(id);
      assertSuccess(data.result);
      return data;
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Status Updated',
      successMessage: 'Engineer marked as en route',
      errorTitle: 'Failed to Mark Engineer En Route',
    },
  });
}

export function useEngineerOnSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const data = await engineerOnSite(id);
      assertSuccess(data.result);
      return data;
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Status Updated',
      successMessage: 'Engineer marked as on site',
      errorTitle: 'Failed to Mark Engineer On Site',
    },
  });
}

export function useEngineeringComplete() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note: string }) => {
      const data = await engineeringComplete({
        id,
        notes: [{ note, createdBy, createdDate: new Date().toISOString() }],
      });
      assertSuccess(data.result);
      return data;
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Engineering Complete',
      successMessage: 'Job marked as complete',
      errorTitle: 'Failed to Mark Engineering Complete',
    },
  });
}

export function useValidationRequested() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: async ({
      id,
      note,
      invoiceReference,
      invoiceAmount,
    }: {
      id: number;
      note: string;
      invoiceReference?: string;
      invoiceAmount?: number;
    }) => {
      const data = await validationRequested({
        id,
        notes: note.trim()
          ? [{ note: note.trim(), createdBy, createdDate: new Date().toISOString() }]
          : undefined,
        invoice:
          invoiceReference?.trim() || invoiceAmount !== undefined
            ? {
                reference: invoiceReference?.trim() || undefined,
                amount: invoiceAmount,
              }
            : undefined,
      });
      assertSuccess(data.result);
      return data;
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      // Mirrors the throw-on-failure idiom used by useEngineerEnRoute/OnSite/
      // EngineeringComplete: mutationFn throws when result.success is false,
      // so isError reflects a business-rule refusal the same way a network
      // failure would. The caller (StatusBar.tsx) still shows its own success
      // and failure toasts, because the failure copy is job-specific business
      // text, not the backend's generic result.message — so both
      // mutationCache handlers must stay silent here.
      skipSuccessToast: true,
      skipErrorToast: true,
    },
  });
}

export function useCancelFromAbort() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelFromAbort(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Cancelled',
      successMessage: 'Job cancelled from aborted status',
    },
  });
}

export function useReplanFromAbort() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => replanFromAbort(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Replanned',
      successMessage: 'Job moved back to planning status',
    },
  });
}

export function useCancelWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    // SC-6645: the response carries a `success` flag that nothing used, so a
    // failed cancel returned 200 and the UI reported "Job Cancelled" while the
    // job stayed active. The backend now throws instead of reporting failure in
    // the body, and this keeps that honest from the client side too: a falsy
    // `success` is treated as a failed mutation rather than a silent no-op.
    mutationFn: async (id: number) => {
      const result = await cancelWorkOrder(id);
      if (result && result.success === false) {
        throw new Error(result.message || 'The job could not be cancelled.');
      }
      return result;
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Cancelled',
      successMessage: 'Job cancelled successfully',
    },
  });
}

export function useDurationChallengeWorkOrder() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: ({
      jobId,
      diffHours,
      windowStartDate,
      windowEndDate,
      attachments,
    }: {
      jobId: number;
      diffHours: number;
      windowStartDate?: string;
      windowEndDate?: string;
      // Optional supporting evidence for the challenge. Callers upload via the
      // shared uploadActionAttachments helper first (as reject/more-info do,
      // which it never throws for) and pass the resulting refs; the backend
      // overwrites S3BucketName. Omitted today by the auto-trigger caller, but
      // plumbed so a UI upload control needs no signature change here (IGNC-1454).
      attachments?: OutboundDurationChallengeBody['attachments'];
    }) => {
      const body: OutboundDurationChallengeBody = {
        id: jobId,
        schedule: {
          duration: diffHours,
          durationUnit: 'hours',
          windowStartDate,
          windowEndDate,
        },
        notes: [
          {
            note: 'Requesting duration challenge due to selected duration exceeding estimate',
            createdBy,
            createdDate: new Date().toISOString(),
          },
        ],
        ...(attachments?.length ? { attachments } : {}),
      };
      return durationChallengeWorkOrder(body);
    },
    onSuccess: () => {
      invalidateJobsList(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.planner.all });
    },
    meta: {
      successTitle: 'Duration Challenge',
      successMessage: 'Duration challenge submitted successfully',
    },
  });
}
